import React, { useCallback, useMemo } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useDispatch } from 'react-redux';
import { getBaseSliceThunks } from 'c-data';
import type { EntityListColumn } from 'c-pagination';
import type { PopDisplays_JobStatus } from 'c-sdk';
import { useCommonTranslation, useModelFieldTranslation } from 'c-translation';
import { paginationTag, sourceIdFilterKey, sourceTypeFilterKey } from './lib';

/**
 * Narrows the table to the record a job belongs to. Writing the values straight into the filter
 * state is all that's needed — EntityFilters already debounce-searches whenever the filters change,
 * so this behaves exactly as if the type and id had been picked by hand.
 */
const FilterToSourceCol: EntityListColumn<PopDisplays_JobStatus>['RenderCell'] = ({ entity }) => {
    const dispatch = useDispatch();
    const t = useCommonTranslation();
    const mt = useModelFieldTranslation();
    const { updatePaginatedListFilter } = useMemo(
        () => getBaseSliceThunks('PopDisplays_JobStatus'),
        [],
    );

    const { statusable_type: statusableType, statusable_id: statusableId } = entity;

    const label = useMemo(() => {
        if (!statusableType) return '';
        return t('Modules.PopDisplays.JobStatus.table.filterToSource', {
            type: mt('PopDisplays_JobStatus', 'statusable_type', statusableType),
        });
    }, [t, mt, statusableType]);

    const onFilter = useCallback(
        e => {
            e.stopPropagation();
            dispatch(
                updatePaginatedListFilter(paginationTag, sourceTypeFilterKey, [statusableType]),
            );
            dispatch(
                updatePaginatedListFilter(paginationTag, sourceIdFilterKey, [String(statusableId)]),
            );
        },
        [dispatch, updatePaginatedListFilter, statusableType, statusableId],
    );

    if (!statusableType || statusableId == null) return null;

    return (
        <Tooltip title={label}>
            <IconButton size="small" onClick={onFilter} aria-label={label}>
                <FilterAltIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};

export default FilterToSourceCol;
